import { baseApi } from "@/api/baseApi";
import { SPEND_FAMILY_PAGE_SIZE, SPEND_TAGS } from "@/api/spend.constraints";
import type {
  ExpiredContractsReport,
  ExpiredContractsReportParams,
  NoContractReport,
  NoContractReportParams,
  SpendFamilyPage,
  SpendAdminSettings,
  SpendListParams,
  SpendOwner,
  SpendSeriesParams,
  SpendSeriesResponse,
  SpendSettings,
  SpendSettingsUpdate,
  SpendSupplierPage,
} from "@/types/spend";

const BASE = "/pf-mainweb/suppliers/spend";
const withParams = (url: string, values: object) => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  });
  return `${url}?${params.toString()}`;
};

export const spendRequests = {
  settings: () => ({ url: `${BASE}/settings`, method: "GET" as const }),
  adminSettings: () => ({ url: `${BASE}/settings/admin`, method: "GET" as const }),
  updateAdminSettings: (data: SpendSettingsUpdate) => ({
    url: `${BASE}/settings/admin`,
    method: "PATCH" as const,
    data,
  }),
  owners: () => ({ url: `${BASE}/owners`, method: "GET" as const }),
  categories: () => ({ url: `${BASE}/categories`, method: "GET" as const }),
  suppliers: (params: SpendListParams) => ({ url: withParams(`${BASE}/suppliers`, params), method: "GET" as const }),
  families: (supplierId: string, ownerUserId?: string) => ({
    url: withParams(`${BASE}/suppliers/${encodeURIComponent(supplierId)}/families`, {
      owner_user_id: ownerUserId,
      offset: 0,
      limit: SPEND_FAMILY_PAGE_SIZE,
    }),
    method: "GET" as const,
  }),
  series: ({ supplierId, ...params }: SpendSeriesParams) => ({
    url: withParams(`${BASE}/suppliers/${encodeURIComponent(supplierId)}/series`, params),
    method: "GET" as const,
  }),
  expiredContractsReport: (params: ExpiredContractsReportParams) => ({
    url: withParams(`${BASE}/reports/expired-contracts`, params),
    method: "GET" as const,
  }),
  noContractReport: (params: NoContractReportParams) => ({
    url: withParams(`${BASE}/reports/no-contract`, params),
    method: "GET" as const,
  }),
};

export const spendApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getSpendSettings: builder.query<SpendSettings, void>({
      query: spendRequests.settings,
      providesTags: [SPEND_TAGS.SETTINGS],
    }),
    getSpendAdminSettings: builder.query<SpendAdminSettings, void>({
      query: spendRequests.adminSettings,
      providesTags: [SPEND_TAGS.SETTINGS],
    }),
    updateSpendAdminSettings: builder.mutation<SpendSettings, SpendSettingsUpdate>({
      query: spendRequests.updateAdminSettings,
      invalidatesTags: Object.values(SPEND_TAGS),
    }),
    getSpendOwners: builder.query<SpendOwner[], void>({
      query: spendRequests.owners,
      providesTags: [SPEND_TAGS.OWNERS],
    }),
    getSpendCategories: builder.query<string[], void>({
      query: spendRequests.categories,
      providesTags: [SPEND_TAGS.SUPPLIERS],
    }),
    getSpendSuppliers: builder.query<SpendSupplierPage, SpendListParams>({
      query: spendRequests.suppliers,
      providesTags: [SPEND_TAGS.SUPPLIERS],
    }),
    getSpendFamilies: builder.query<SpendFamilyPage, { supplierId: string; ownerUserId?: string }>({
      query: ({ supplierId, ownerUserId }) => spendRequests.families(supplierId, ownerUserId),
      providesTags: [SPEND_TAGS.FAMILIES],
    }),
    getSpendSeries: builder.query<SpendSeriesResponse, SpendSeriesParams>({
      query: spendRequests.series,
      providesTags: [SPEND_TAGS.SERIES],
    }),
    getExpiredContractsReport: builder.query<ExpiredContractsReport, ExpiredContractsReportParams>({
      query: spendRequests.expiredContractsReport,
      providesTags: [SPEND_TAGS.REPORTS],
    }),
    getNoContractReport: builder.query<NoContractReport, NoContractReportParams>({
      query: spendRequests.noContractReport,
      providesTags: [SPEND_TAGS.REPORTS],
    }),
  }),
});

export const {
  useGetSpendSettingsQuery,
  useGetSpendAdminSettingsQuery,
  useUpdateSpendAdminSettingsMutation,
  useGetSpendOwnersQuery,
  useGetSpendCategoriesQuery,
  useGetSpendSuppliersQuery,
  useGetSpendFamiliesQuery,
  useGetSpendSeriesQuery,
  useGetExpiredContractsReportQuery,
  useGetNoContractReportQuery,
} = spendApi;
